// Core Modules
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map } from 'rxjs';

// Services
import { AuthService } from '@core/services/auth/auth.service';

// Layout Components
import { HeaderComponent } from '@layout/header/header.component';
import { SideNavComponent } from '@layout/side-nav/side-nav.component';
import { FooterComponent } from '@layout/footer/footer.component';

// Shared Components
import { ToastComponent } from '@shared/components/toast/toast.component';

// Enums
import { MainNavigationEnum } from '@shared/enum/main-navigation-url.enum';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [HeaderComponent, SideNavComponent, FooterComponent, RouterOutlet, ToastComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {
  // Injectors
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  /** Hide the header/footer chrome on the login (or empty) route. */
  private readonly hideNavigation = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => [`/${MainNavigationEnum.LOGIN}`, '/'].includes(event.urlAfterRedirects))
    ),
    { initialValue: true }
  );

  // AuthService observables (frozen) consumed read-only as signals.
  private readonly isAuthInProgress = toSignal(this.auth.isAuthenticationInProgress$, { initialValue: false });
  private readonly isClientNavInProgress = toSignal(this.auth.isClientNavigationInProgress$, { initialValue: false });

  /** Combined shell state consumed by the template. */
  public readonly appState = computed(() => {
    const hideNavigation = this.hideNavigation();
    return {
      isAuthInProgress: this.isAuthInProgress(),
      hideNavigation,
      showHeader: !hideNavigation,
      showContent: !this.isClientNavInProgress(),
      showFooter: !hideNavigation,
    };
  });
}
